<script>
/**
 * A orb do Modo de Voz — pequena, acima do composer.
 *
 * [feedback do usuário, 04/09 — pivô ChatGPT] "podemos aproveitar a orb que
 * ja temos no fee fixo, deixando ela bem pequena mesmo... acho que la tem
 * cores diferentes ja quando eh eu quem falo e quando eh a IA". Decalque de
 * `fee-fixo/components/fee-fixo-roleplay-orb/fee-fixo-roleplay-orb.vue`
 * (respiração + 2 ripples), reduzida bem abaixo do original daquele módulo
 * (168px) — aqui é só um acento de ~36px acima do campo.
 *
 * Esta é a TERCEIRA forma deste componente nesta rodada de trabalho: nasceu
 * esfera, virou nuvem com blur (rejeitada — "suja, borrada"), virou espectro
 * de barras (rejeitado — o usuário queria a esfera de volta, "bem pequena").
 * A LÓGICA do espectro de barras não foi perdida: migrou para
 * `composer-ditar-espectro.vue`, que é a visualização do modo DITAR (onda
 * contínua full-width) — esta orb aqui é EXCLUSIVA do Modo de Voz.
 *
 * `usuario` é neutro (cinza); `ia` usa o ramp `--primary-*` — cor dinâmica
 * por escritório (white-label), nunca hex fixo.
 *
 * [spec-017 T004, 09/09] Terceiro modo: `aguardando` — sessão de Modo de Voz
 * ATIVA, mas o microfone NÃO está armado (nem o usuário fala, nem a IA).
 * Acontece em duas janelas reais: a IA foi transcrita e ainda não respondeu
 * (`transcrevendo`/espera da resposta) e o respiro entre o fim da fala da IA
 * e o re-arme automático do mic (`fala-da-ia.js::_aoFilaEsvaziar`). Sem este
 * modo, `estadoDaFala` fica `null` nessas janelas e a orb inteira SOME — o
 * consultor vê a mesma tela de "Modo de Voz nunca começou" enquanto a sessão
 * continua viva por baixo (o bug que o owner relatou: "na confirmação o modo
 * voz saiu novamente"). Visual: esfera neutra parada (sem respiração, sem
 * ripples) — "pausada", nunca "escutando" nem "falando".
 *
 * ★ [spec-008 T060, 17/09] Quarto modo: `consultando` — a pergunta saiu e a
 * resposta não voltou. Ele existe porque o indicador precisa dizer as TRÊS
 * coisas que acontecem numa sessão paga (**ouvindo / IA falando /
 * consultando**) e `aguardando` só diz "pausada": a esfera imóvel e
 * apagada, exibida enquanto a AAWZ vai buscar o dado, diz ao consultor que o
 * microfone está fechado e nada está acontecendo — que é o oposto do fato.
 * Visual: esfera neutra clara com um ARCO girando em `--primary`, o idioma
 * universal de "trabalhando".
 *
 * ★ [spec-008 T060] `usuario` passou a significar **"o microfone é seu"** —
 * cobre ouvir em silêncio E falar. Não são dois desenhos porque a escala AO
 * VIVO (`nivel`, o RMS real) já distingue os dois: parada, ela diz "te
 * escuto"; pulsando, "estou te ouvindo agora". Um quinto modo só para o
 * silêncio seria uma segunda esfera neutra idêntica à primeira.
 */
export const MODO_USUARIO = 'usuario'
export const MODO_IA = 'ia'
export const MODO_AGUARDANDO = 'aguardando'
export const MODO_CONSULTANDO = 'consultando'
const MODOS_VALIDOS = [
  MODO_USUARIO,
  MODO_IA,
  MODO_AGUARDANDO,
  MODO_CONSULTANDO,
]

// [feedback do usuário, 04/09 — 3ª rodada] "a orb se mexe quando eu falo?" —
// não mexia: a respiração era um `@keyframes` fixo, sem ligação nenhuma com
// `nivel`. Mesmo ganho já validado no espectro do Ditar — fala normal fica
// bem abaixo de 1 de RMS (o limiar de silêncio do VAD é 0.02).
const FATOR_DE_GANHO = 6
const ESCALA_MINIMA = 1
const ESCALA_MAXIMA = 1.35

export default {
  name: 'IntelligenceV2ComposerVozOrb',
  props: {
    mode: {
      type: String,
      required: true,
      validator: (value) => MODOS_VALIDOS.includes(value),
    },
    // RMS do microfone (0-1). Só é amostrado de verdade enquanto o USUÁRIO
    // fala; com a IA falando não há medidor sobre o áudio de playback (o
    // valor fica em 0, e a orb aí só respira no ritmo decorativo).
    nivel: {
      type: Number,
      default: 0,
    },
  },
  computed: {
    classesDoOrb() {
      return `composer-voz-orb-wrap--${this.mode}`
    },
    // Escala AO VIVO — soma-se (multiplicativamente, via composição de
    // transforms) à respiração decorativa do `.composer-voz-orb` interno,
    // que continua rodando sozinha.
    escalaAoVivo() {
      const amplificado = Math.min(this.nivel * FATOR_DE_GANHO, 1)
      return ESCALA_MINIMA + amplificado * (ESCALA_MAXIMA - ESCALA_MINIMA)
    },
    // `transform` inline substitui qualquer `transform` de classe — por isso
    // a classe estática abaixo não declara `transform` nenhum.
    //
    // ★ [T061] A centralização SAIU daqui (era `translateX(-50%)`, par do
    // `left: 50%` de quando a orb flutuava por cima da conversa). Em fluxo
    // ela é centrada por `margin: 0 auto`, que é layout — e layout não pode
    // depender de um `transform` que a escala ao vivo reescreve 60×/s.
    estiloDoWrap() {
      return { transform: `scale(${this.escalaAoVivo})` }
    },
    // Ripple é "estou captando algo agora" (RMS do usuário ou o áudio da
    // IA) — em `aguardando` não há nada sendo captado, e em `consultando`
    // quem trabalha é o servidor, não o microfone: nos dois os anéis
    // mentiriam captação que não existe. A esfera fica parada (ver
    // `animation: none` no CSS dos dois), sem os dois ripples.
    mostrarRipples() {
      return this.mode !== MODO_AGUARDANDO && this.mode !== MODO_CONSULTANDO
    },
    // ★ [T060] O arco girando é EXCLUSIVO de `consultando` — ele afirma
    // "estou trabalhando nisso", e afirmá-lo em qualquer outro estado seria
    // atividade inventada. É o que separa visualmente "fui buscar" (arco) de
    // "pausada" (esfera apagada e imóvel).
    mostrarArco() {
      return this.mode === MODO_CONSULTANDO
    },
  },
}
</script>

<template>
  <div
    class="composer-voz-orb-wrap"
    :class="classesDoOrb"
    :style="estiloDoWrap"
    data-teste="composer-voz-orb"
    aria-hidden="true"
  >
    <template v-if="mostrarRipples">
      <div class="composer-voz-orb-ripple" />
      <div class="composer-voz-orb-ripple composer-voz-orb-ripple--delay" />
    </template>
    <div v-if="mostrarArco" class="composer-voz-orb-arco" />
    <div class="composer-voz-orb" />
  </div>
</template>

<style lang="sass" scoped>
// ⚠️ [bug corrigido, 04/09] O `class="send-message-voz-orb"` que o pai
// (`send-message.vue`) passava ao filho caía NESTE MESMO elemento raiz (o Vue
// funde `class` do pai com a raiz do filho, mesmo sem `inheritAttrs`) — duas
// regras de posicionamento colidiam no mesmo nó. Esta regra é a ÚNICA dona do
// posicionamento — `send-message.vue` não estiliza mais este elemento.
// ★ [spec-008 T061, 17/09] A orb ENTROU NO FLUXO — e esta é a mudança que o
// print do owner pediu. Ela era `position: absolute; left: 50%; bottom:
// calc(100% + 24px)`: fora do fluxo, ancorada no `.send-message-wrapper`, ela
// pousava 24px ACIMA do composer, ou seja, **dentro da área de mensagens** —
// e cobria linha de tabela e texto da resposta que o consultor estava lendo.
//
// Em fluxo, a caixa de 44px+16px passa a ser altura do composer. O rodapé da
// `/ia` é `flex-shrink: 0` numa coluna flex, então a área de mensagens (o
// `flex-grow: 1` ao lado) encolhe exatamente esse tanto sozinha: o "respiro
// equivalente" não precisa de número repetido em lugar nenhum.
//
// ⚠️ `position: relative` continua obrigatório — os dois ripples e o arco são
// `absolute` e resolvem contra esta caixa. Trocar por `static` os jogaria no
// `.send-message-wrapper`, de volta por cima da conversa.
.composer-voz-orb-wrap
  position: relative
  // Centrada por MARGIN, não por `transform`: o `transform` inline é reescrito
  // a cada amostra de RMS (`estiloDoWrap`), e pendurar layout nele fazia a orb
  // saltar horizontalmente junto com a pulsação.
  margin: 0 auto 16px
  width: 44px
  height: 44px
  display: flex
  align-items: center
  justify-content: center
  pointer-events: none
  // A escala AO VIVO (RMS real, via `:style`) muda a cada amostra; animar a
  // transição suaviza o "pulo" entre elas em vez de saltar quadro a quadro —
  // mesma receita do espectro do Ditar.
  transition: transform 0.08s linear

.composer-voz-orb
  width: 36px
  height: 36px
  border-radius: 50%
  animation: composerVozOrbBreathe 2.4s ease-in-out infinite

.composer-voz-orb-ripple
  position: absolute
  width: 44px
  height: 44px
  border-radius: 50%
  border: 1.5px solid transparent
  opacity: 0
  animation: composerVozOrbRipple 2.2s ease-out infinite

  &--delay
    animation-delay: 1.1s

.composer-voz-orb-wrap--usuario
  .composer-voz-orb
    background: radial-gradient(circle at 32% 26%, var(--neutral-100), var(--neutral-300) 55%, var(--neutral-400))
    box-shadow: inset 0 -4px 8px rgba(0, 0, 0, .1)
  .composer-voz-orb-ripple
    border-color: var(--neutral-400)

.composer-voz-orb-wrap--ia
  .composer-voz-orb
    background: radial-gradient(circle at 32% 26%, var(--primary-light), var(--primary) 55%, var(--primary-dark))
    box-shadow: 0 4px 12px var(--primary-light), inset 0 -4px 8px rgba(0, 0, 0, .18)
  .composer-voz-orb-ripple
    border-color: var(--primary)

// [spec-017 T004] "Pausada" — mesmo ramp neutro de `--usuario` (não é o
// `--primary` de `--ia`: quem fala é ninguém agora, então a cor não pode
// sugerir nem usuário nem IA), porém SEM respirar (`animation: none` —
// congela no `scale(1)` do frame 0%/100%, nunca inicia o ciclo) e com
// opacidade reduzida. Os ripples já saem pelo `v-if` do template
// (`mostrarRipples`); nada aqui precisa escondê-los de novo.
.composer-voz-orb-wrap--aguardando
  .composer-voz-orb
    background: radial-gradient(circle at 32% 26%, var(--neutral-100), var(--neutral-300) 55%, var(--neutral-400))
    box-shadow: inset 0 -4px 8px rgba(0, 0, 0, .1)
    opacity: .5
    animation: none

// ★ [T060] "Consultando" — a pergunta saiu, a resposta não voltou.
//
// A esfera é neutra e CLARA (mais clara que a de `--usuario`): quem fala aqui
// não é nem o consultor nem a IA, então a cor não pode sugerir nenhum dos
// dois — e ela não respira, porque respirar é o sinal de microfone aberto.
// O que afirma atividade é o ARCO, e só ele: um anel de 44px com três lados
// transparentes girando em `--primary`, que é o idioma que todo mundo já lê
// como "trabalhando". Cor por TOKEN — `--primary` é white-label por
// escritório, nunca hex fixo.
.composer-voz-orb-wrap--consultando
  .composer-voz-orb
    background: radial-gradient(circle at 32% 26%, var(--neutral-50), var(--neutral-200) 55%, var(--neutral-300))
    box-shadow: inset 0 -4px 8px rgba(0, 0, 0, .06)
    animation: none

.composer-voz-orb-arco
  position: absolute
  width: 44px
  height: 44px
  border-radius: 50%
  border: 2px solid transparent
  border-top-color: var(--primary)
  animation: composerVozOrbGirar 1s linear infinite

@keyframes composerVozOrbGirar
  from
    transform: rotate(0deg)
  to
    transform: rotate(360deg)

@keyframes composerVozOrbBreathe
  0%, 100%
    transform: scale(1)
  50%
    transform: scale(1.12)

@keyframes composerVozOrbRipple
  0%
    transform: scale(1)
    opacity: .4
  100%
    transform: scale(1.35)
    opacity: 0

// Quem pediu menos movimento continua vendo os ESTADOS — só sem animação. O
// arco parado ainda distingue "consultando" das outras esferas pela forma
// (o anel), que é o que ele precisa fazer aqui.
@media (prefers-reduced-motion: reduce)
  .composer-voz-orb,
  .composer-voz-orb-ripple,
  .composer-voz-orb-arco
    animation: none
</style>
